/**
 * GenerationReadinessStrip
 *
 * The Create/Edit Strategy dialog's honest answer to «can this generate?».
 *
 * Program: strategy-intake-feasibility (design brief
 * `.design/strategy-intake-feasibility/DESIGN_BRIEF.md`, mocks
 * `mockups/create-strategy-readiness-{ready,blocked}.html`), Task 10 —
 * AC-005/AC-006/AC-007/AC-008/AC-013/AC-024.
 *
 * The strip is a PURE re-presentation of `assessStrategyGenerationFeasibility`
 * (FP-10): it holds no state, fetches nothing and decides nothing. Everything it
 * shows is derived from the `FeasibilityResult` the dialog computed off the
 * selection the submit handler will serialise — «what you see is what is sent»
 * (FP-7, experience principle 1).
 *
 * Three states, never a fourth (AC-013):
 * - `checking` — the pickers are still hydrating. The strip says «Checking…»
 *   and the dialog's primary stays ENABLED (strategy-blocks designer invariant
 *   5: a primary is never blocked on data hydration).
 * - `ready`   — the request can succeed.
 * - `blocked` — deterministic blockers only; each row NAMES the reason and,
 *   where a one-click fix exists, offers it inline (experience principle 2).
 *
 * Warnings never gate anything — they are advice, rendered in the warning tone.
 *
 * Layout: ONE outlined Paper. Blocker/warning rows are divider rows inside it,
 * never nested cards (impeccable `nested-cards`, brief detector self-check).
 * Colours come from theme tokens only.
 *
 * TWO layouts, one verdict (W12 Part B, Task B6):
 * - `strip` (default) — the shipped four-cell fact grid. Edit mode and every
 *   other caller keep it byte-for-byte.
 * - `rows`  — the create wizard's review step: header chip + provenance line,
 *   the blocker panel FIRST with its contained fix, then one row per fact
 *   (icon · label · value · detail · «Edit», which jumps to the step owning the
 *   value). Owner ruling 2026-09-10: readiness is a plain row list, blocker
 *   first with its fix. The facts, their testids and their values are SHARED
 *   with the strip — only the arrangement differs.
 */

import React from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { GLOBAL_INSIGHT_SCOPE } from '@modules/shared-types';
import type { FeasibilityBlocker, FeasibilityResult, FeasibilityWarning } from '@modules/shared-types';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';

export type ReadinessLayout = 'strip' | 'rows';

/**
 * The facts the verdict reports. `strip` renders four cells (`analysis`,
 * `routes`, `variables`, `social-proof`); `rows` adds `insight-variables`,
 * which the strip folds into the `variables` sub-line for want of a fifth cell.
 */
export type ReadinessFactKey =
  | 'analysis'
  | 'routes'
  | 'insights'
  | 'insight-variables'
  | 'variables'
  | 'social-proof';

export interface GenerationReadinessStripProps {
  /** The verdict, computed by the dialog from the SUBMITTED selection. */
  result: FeasibilityResult;
  /** Name of the pinned insight analysis (`undefined` while nothing is pinned). */
  analysisName?: string;
  /**
   * Names of the task routes the pinned analysis makes AVAILABLE — the
   * denominator of the routes fact cell and of the inline fix («Select all N
   * routes»). The numerator is `result.facts.routes`, i.e. what is sent.
   */
  routeNames: string[];
  /** Social-proof entities in the submitted selection, per kind. */
  socialProof: { reviews: number; ratings: number; studyCases: number; bigNumbers: number };
  /**
   * Accepted insight-derived variables in the chosen generation — the
   * denominator of «insight-derived: K of M». Omitted (or `undefined`) when no
   * generation is chosen, and the cell then reports the bare count.
   */
  insightVariablesAccepted?: number;
  /**
   * W13 IS2 (AC-W13-07 / AC-W13-03): the theses this run is grounded in, out of
   * the ones the owner could choose from. ABSENT = no thesis selection was
   * expressed — the strip then reads exactly as it did before W13 (AC-W13-34).
   *
   * The create wizard does NOT pass it: its review step states the same numbers
   * on the «Also sent» «Insights» row (which also carries the quotes switch),
   * and one screen states a number once.
   */
  insightTheses?: { selected: number; available: number };
  /**
   * W13 IS2 (REVIEW tier 2, MF-5 — designer invariant 26): re-check every thesis
   * of ONE scope, so the `task_without_theses` blocker carries the click that
   * ends it instead of prose only. `scopeId` is the blocker's task id, or
   * `undefined` for the global / «nothing selected» form. Omitted = no fix is
   * offered (edit mode has no thesis step to fix it on).
   */
  onSelectAllTheses?: (scopeId?: string) => void;
  /** Task id → name, so the fix can say «Select all theses of "Build muscle"». */
  insightScopeNames?: Record<string, string>;
  /**
   * Project variables that carry a task scope in the WHOLE project package —
   * the number that comes back when routes are selected again. Feeds the
   * «N task-scoped variables are not sent…» info row; without it the row states
   * the rule without the size.
   */
  taskScopedVariablesWhenSelected?: number;
  /** Selects every available task route (fix for `no_task_routes`). */
  onSelectAllRoutes: () => void;
  /** Opens «Source data (AI)» and scrolls to it. Unused by the `rows` layout,
   * where every fact row carries its own «Edit» (so there is no dead end). */
  onAdjust: () => void;
  /**
   * `strip` (default) = the shipped fact grid; `rows` = the create wizard's
   * review list. Anything that does not ask for `rows` is unchanged.
   */
  layout?: ReadinessLayout;
  /**
   * `rows` layout only: renders an «Edit» per fact row that jumps to the step
   * owning the value. Omitted → no Edit affordance at all.
   */
  onEdit?: (fact: ReadinessFactKey) => void;
  /**
   * The LAUNCH verdict (`launchReadinessStatus`) when the surface hosting this
   * strip also hosts «Create with AI» — the create wizard's review step. The
   * status chip then reports the same thing the primary does, instead of the
   * feasibility contract alone, which knows nothing about the name field, the
   * variable-package gate or the SPEC-019 library conflict (m46: a chip reading
   * «Ready to generate» over a disabled primary). Omitted → the chip keeps
   * reporting `readinessStatusLabel(result)` (edit mode is untouched).
   */
  launchStatus?: LaunchReadiness;
  'data-testid'?: string;
}

/**
 * DOM id of a blocker row — the dialog points the primary's
 * `aria-describedby` at it, so the reason a button is disabled is announced
 * (strategy-blocks designer invariant 14, AC-007).
 */
export function readinessBlockerElementId(code: string): string {
  return `strategy-readiness-blocker-${code}`;
}

/**
 * W13 (IS1): one verdict can now carry SEVERAL rows of the same code — a thesis
 * selection can starve two tasks, and every unknown insight id is named on its
 * own row. The row id keeps the shipped `strategy-readiness-blocker-<code>`
 * shape for the codes that appear once and gains the scope that makes it unique
 * for the ones that repeat, so nothing that reads the old ids moves.
 */
export function readinessBlockerRowId(blocker: FeasibilityBlocker): string {
  const scope = blocker.taskId ?? blocker.insightId;
  return readinessBlockerElementId(scope ? `${blocker.code}-${scope}` : blocker.code);
}

/**
 * W13 (fix round 1 / I1): warnings repeat by code too — an over-wide selection
 * warns per SCOPE (global + one per task), and an optionless insight variable
 * warns per VARIABLE. `scopeId` is what makes each row addressable.
 */
export function readinessWarningRowId(warning: FeasibilityWarning): string {
  return `strategy-readiness-warning-${warning.code}${warning.scopeId ? `-${warning.scopeId}` : ''}`;
}

/** Row ids for ONE list, de-duplicated: the first of a repeat keeps the plain id. */
function uniqueRowIds<T>(items: T[], idOf: (item: T) => string): string[] {
  const used = new Map<string, number>();
  return items.map((item) => {
    const base = idOf(item);
    const seen = used.get(base) ?? 0;
    used.set(base, seen + 1);
    return seen === 0 ? base : `${base}-${seen + 1}`;
  });
}

/**
 * The feasibility messages are written as «<what is wrong>. <why it matters>.»
 * — the first sentence is the row title, the rest is its body. Splitting here
 * (instead of storing two fields) keeps the contract a single string that the
 * server 400 and the CLI can print verbatim.
 */
export function splitBlockerMessage(message: string): { title: string; detail: string } {
  const cut = message.indexOf('. ');
  if (cut < 0) return { title: message.replace(/\.$/, ''), detail: '' };
  return { title: message.slice(0, cut), detail: message.slice(cut + 2) };
}

/** The three words the feasibility contract can say about a request (AC-013). */
export type ReadinessStatusLabel =
  | 'Ready to generate'
  | 'Checking…'
  | `Not ready: ${number} blocker${'' | 's'}`;

/** «Not ready: 1 blocker» / «Not ready: 2 blockers» (AC-006, AC-024). */
export function readinessStatusLabel(result: FeasibilityResult): ReadinessStatusLabel {
  if (result.state === 'checking') return 'Checking…';
  if (result.state === 'ready') return 'Ready to generate';
  const count = result.blockers.length;
  return `Not ready: ${count} blocker${count === 1 ? '' : 's'}`;
}

/**
 * The launch primary is gated by MORE than the feasibility contract: the
 * strategy needs a name, and the insight-variable package has its own gate
 * (stale / incomplete / empty generation). A surface that shows one verdict
 * next to that primary — the quick-create chip (W12 Part B, Task B7) — must
 * derive BOTH the wording and the blocked flag from the same place, or it can
 * end up painted red while reading «Ready to generate».
 */
export type LaunchReadinessLabel =
  | ReadinessStatusLabel
  | 'Not ready: name required'
  | 'Not ready: variable package'
  | 'Not ready: library conflict'
  | 'Not ready: already created'
  | 'Creating…';

/**
 * The ONE reason the primary is unavailable while the screen is still
 * hydrating. Named, never silent (designer invariant 14) — and never a red
 * failure chip, which is what invariant 5 actually forbids (Task B9d fix 6).
 */
export const LAUNCH_HYDRATION_REASON = 'Loading insight analyses…';

/**
 * Ruling FW9-R5: a create whose generation FAILED left a real strategy document
 * behind, and the dialog now stays open on that failure (FW9-R3) — so both
 * launch actions would create a SECOND strategy. The ONE sentence that says why
 * they are off, shared by the footer line and this verdict so the surfaces
 * cannot word it differently.
 */
export const LAUNCH_ALREADY_CREATED_REASON =
  'The strategy was already created — close the dialog to open it.';

export interface LaunchReadiness {
  label: LaunchReadinessLabel;
  /** Exactly the primary's disabled state — see `WizardFooter`'s review `blocked`. */
  blocked: boolean;
  /** Set only while hydrating: the line the footer renders under the primary. */
  reason?: string;
}

export interface LaunchReadinessInput {
  result: FeasibilityResult;
  /** The Name field is empty (it lives on the same screen — a one-action gate). */
  nameMissing: boolean;
  /** `gatingVariablePackageReason` is set: a real gate, never a hydration artifact. */
  variablePackageBlocked: boolean;
  /**
   * SPEC-019: a materialized library example whose block type the «Block types»
   * whitelist excludes. `handleSubmit` refuses such a request (and the server
   * re-enforces it), so it gates the primary exactly like the gates above —
   * the review/quick chip may never read «Ready to generate» over it (m46).
   */
  libraryConflict?: boolean;
  /** A submit is in flight — the primary reads «Saving…» and is disabled. */
  submitting: boolean;
  /**
   * Ruling FW9-R5: this dialog already created its strategy (a non-refused
   * generation failure keeps the document and keeps the dialog open), so a
   * second launch would create a second document. The chip must say so — m46:
   * it may never read «Ready to generate» over a disabled primary.
   */
  alreadyCreated?: boolean;
  /**
   * The screen has not finished hydrating: the analysis list is still loading,
   * or it has loaded with analyses in it and the auto-pin has not resolved yet.
   * A project that genuinely has NO completed analysis is not hydrating — it
   * gets the real verdict, which names that (quick create, Task B7).
   */
  hydrating?: boolean;
}

export function launchReadinessStatus(input: LaunchReadinessInput): LaunchReadiness {
  if (input.submitting) return { label: 'Creating…', blocked: true };
  // FW9-R5 comes before every readiness gate: nothing about the inputs matters
  // once a document exists — the operator's next step is to close the dialog.
  if (input.alreadyCreated) {
    return {
      label: 'Not ready: already created',
      blocked: true,
      reason: LAUNCH_ALREADY_CREATED_REASON,
    };
  }
  // Hydration comes before every INPUT gate below: they would be counting
  // blockers against data that has not arrived, and the operator would be
  // greeted by a red «Not ready: 2 blockers» before the screen has loaded.
  // (Only the two states that are about this dialog's own run — a submit in
  // flight and FW9-R5's already-created document — are read before it.)
  if (input.hydrating) {
    return { label: 'Checking…', blocked: true, reason: LAUNCH_HYDRATION_REASON };
  }
  // A missing name gates the primary whatever the contract says — including
  // while it is still `checking`, where no other gate is claimed yet.
  if (input.nameMissing) return { label: 'Not ready: name required', blocked: true };
  // SPEC-019 (m46): the whitelist×library contradiction is refused on submit,
  // so it gates the primary whatever the feasibility contract says — including
  // while that contract is still `checking`, which does not block on its own.
  if (input.libraryConflict) return { label: 'Not ready: library conflict', blocked: true };
  if (input.result.state === 'blocked') {
    return { label: readinessStatusLabel(input.result), blocked: true };
  }
  // `checking` never blocks the primary (designer invariant 5: no primary is
  // gated on hydration), so the chip stays neutral too.
  if (input.result.state === 'checking') return { label: 'Checking…', blocked: false };
  if (input.variablePackageBlocked) return { label: 'Not ready: variable package', blocked: true };
  return { label: 'Ready to generate', blocked: false };
}

/**
 * The footer helper sentence (AC-007): «Create with AI is disabled: 1 blocker
 * in Generation readiness (no task routes selected).» The parenthetical lists
 * the blocker titles, in the contract's order, lower-cased.
 */
export function readinessDisabledHelperText(result: FeasibilityResult): string | null {
  if (result.state !== 'blocked' || result.blockers.length === 0) return null;
  const count = result.blockers.length;
  const reasons = result.blockers
    .map((blocker) => splitBlockerMessage(blocker.message).title)
    .map((title) => title.charAt(0).toLowerCase() + title.slice(1))
    .join(', ');
  return `Create with AI is disabled: ${count} blocker${count === 1 ? '' : 's'} in Generation readiness (${reasons}).`;
}

/** Sub-line of the «Task routes» cell — selected vs available, never a guess. */
function routesDetail(selected: number, routeNames: string[]): string {
  const available = routeNames.length;
  if (available === 0) {
    return selected === 0 ? 'no routes available — pin an insight analysis' : `${selected} selected`;
  }
  if (selected === 0) return `none of the ${available} available routes selected`;
  if (selected >= available) return routeNames.join(' · ');
  return `${selected} of ${available} available routes selected`;
}

/**
 * The facts, computed ONCE for both layouts. The only difference the layout
 * makes is the `variables` sub-line: the strip folds the insight-derived ratio
 * into it because it has four cells and five facts, while the rows layout gives
 * that ratio a row of its own (and an Edit that reaches the step owning it).
 */
interface ReadinessFact {
  key: ReadinessFactKey;
  label: string;
  value: string;
  detail: string;
}

function buildReadinessFacts(args: {
  result: FeasibilityResult;
  analysisName?: string;
  routeNames: string[];
  socialProof: { reviews: number; ratings: number; studyCases: number; bigNumbers: number };
  insightVariablesAccepted?: number;
  insightTheses?: { selected: number; available: number };
  layout: ReadinessLayout;
}): ReadinessFact[] {
  const {
    result, analysisName, routeNames, socialProof, insightVariablesAccepted, layout,
  } = args;
  const { facts } = result;

  const socialProofTotal = socialProof.reviews + socialProof.ratings
    + socialProof.studyCases + socialProof.bigNumbers;

  const analysisDetail = analysisName
    ? (routeNames.length > 0
      ? `pinned · covers all ${routeNames.length} route${routeNames.length === 1 ? '' : 's'}`
      : 'pinned')
    : 'not pinned — generation needs exactly one analysis';

  const insightRatio = insightVariablesAccepted === undefined
    ? `${facts.insightVariables}`
    : `${facts.insightVariables} of ${insightVariablesAccepted}`;

  const variableBreakdown = `${facts.globalVariables} global (${facts.requiredGlobalIds.length} required)`
    + ` + ${facts.taskScopedVariables} task-scoped`;
  const projectDetail = `project: ${variableBreakdown}`;

  const analysis: ReadinessFact = {
    key: 'analysis',
    label: 'Insight analysis',
    value: analysisName || '—',
    detail: analysisDetail,
  };
  const routes: ReadinessFact = {
    key: 'routes',
    label: 'Task routes',
    value: String(facts.routes),
    detail: routesDetail(facts.routes, routeNames),
  };
  /**
   * The `rows` layout gives the insight-derived ratio a row of its OWN, so the
   * variables row is about PROJECT variables and says so — one noun, one number
   * (Task B9d must-fix 4: «14 + 0» next to «copy of the 20 variables» made the
   * one screen whose job is «what you see is what is sent» unanswerable).
   * The `strip` layout, which has four cells for five facts, is untouched.
   */
  const variables: ReadinessFact = layout === 'rows'
    ? {
      key: 'variables',
      label: 'Project variables',
      value: String(facts.projectVariables),
      detail: variableBreakdown,
    }
    : {
      key: 'variables',
      label: 'Variables',
      value: `${facts.projectVariables} + ${facts.insightVariables}`,
      detail: `${projectDetail} · insight-derived: ${insightRatio}`,
    };
  const socialProofFact: ReadinessFact = {
    key: 'social-proof',
    label: 'Social proof',
    value: String(socialProofTotal),
    detail: `${socialProof.reviews} reviews · ${socialProof.ratings} ratings`
      + ` · ${socialProof.studyCases} study cases · ${socialProof.bigNumbers} big numbers`,
  };

  /**
   * W13 IS2: what the run is GROUNDED in. Rendered only when a selection was
   * expressed — a strategy nobody narrowed says nothing here, exactly as
   * before (AC-W13-34).
   */
  const insightTheses: ReadinessFact | null = (() => {
    const theses = args.insightTheses;
    if (!theses) return null;
    // A selection WIDER than the universe means the analysis' thesis list is
    // only partly hydrated. Saying «4 of 2» would be nonsense and clamping it to
    // «4 of 4» would be a lie, so the denominator is dropped and named instead.
    if (theses.selected > theses.available) {
      return {
        key: 'insights',
        label: 'Insight theses',
        value: `${theses.selected} theses`,
        detail: 'the pinned analysis’ thesis list is only partly loaded — the total is unknown',
      };
    }
    return {
      key: 'insights',
      label: 'Insight theses',
      value: `${theses.selected} of ${theses.available} theses`,
      detail: theses.selected >= theses.available
        ? 'every thesis of the pinned analysis grounds this run'
        : 'the theses this run is grounded in — the rest are not sent',
    };
  })();

  if (layout === 'strip') {
    return insightTheses
      ? [routes, variables, socialProofFact, analysis, insightTheses]
      : [routes, variables, socialProofFact, analysis];
  }

  return [
    analysis,
    routes,
    ...(insightTheses ? [insightTheses] : []),
    {
      key: 'insight-variables',
      label: 'Insight-derived variables',
      value: insightRatio,
      detail: insightVariablesAccepted === undefined
        ? 'no variable set chosen — product variables only'
        : 'accepted variables of the chosen variable set',
    },
    variables,
    socialProofFact,
  ];
}

/** One fact cell: label, value, sub-line. Divider on top, no card of its own. */
function Fact({ testId, label, value, detail }: {
  testId: string;
  label: string;
  value: string;
  detail: string;
}): React.ReactElement {
  return (
    <Box data-testid={testId} sx={{ borderTop: 1, borderColor: 'divider', pt: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography
        data-testid={`${testId}-value`}
        sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.3, wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {detail}
      </Typography>
    </Box>
  );
}

/** A divider row inside the strip: icon + text (+ optional inline action). */
function ReadinessRow({ id, testId, icon, title, detail, action }: {
  id?: string;
  testId: string;
  icon: React.ReactNode;
  title?: string;
  detail: string;
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <Stack
      id={id}
      data-testid={testId}
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
      sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5, mt: 1.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', mt: '2px' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          {title ? (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{title}</Typography>
          ) : null}
          {detail ? (
            <Typography variant="body2" color="text.secondary">{detail}</Typography>
          ) : null}
        </Box>
      </Box>
      {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
    </Stack>
  );
}

/** One fact ROW (rows layout): icon · label · value + detail · «Edit». */
function FactRow({ fact, blocked, onEdit }: {
  fact: ReadinessFact;
  blocked: boolean;
  onEdit?: (fact: ReadinessFactKey) => void;
}): React.ReactElement {
  return (
    <Box
      data-testid={`strategy-readiness-fact-${fact.key}`}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        py: 1.25,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', mt: '2px' }}>
        {blocked
          ? <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
          : <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>
        {fact.label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          data-testid={`strategy-readiness-fact-${fact.key}-value`}
          variant="body2"
          sx={{ fontWeight: 500, wordBreak: 'break-word' }}
          color={blocked ? 'error.main' : 'text.primary'}
        >
          {fact.value}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {fact.detail}
        </Typography>
      </Box>
      {onEdit && (
        <Button
          variant="text"
          size="small"
          onClick={() => onEdit(fact.key)}
          aria-label={`Edit ${fact.label}`}
          data-testid={`strategy-wizard-review-edit-${fact.key}`}
          sx={{ flexShrink: 0, textTransform: 'none', color: 'primary.dark' }}
        >
          Edit
        </Button>
      )}
    </Box>
  );
}

export const GenerationReadinessStrip: React.FC<GenerationReadinessStripProps> = ({
  result,
  analysisName,
  routeNames,
  socialProof,
  insightVariablesAccepted,
  insightTheses,
  onSelectAllTheses,
  insightScopeNames,
  taskScopedVariablesWhenSelected,
  onSelectAllRoutes,
  onAdjust,
  layout = 'strip',
  onEdit,
  launchStatus,
  'data-testid': dataTestId = 'strategy-readiness-strip',
}) => {
  const { state, blockers, warnings } = result;
  const blocked = state === 'blocked';
  const checking = state === 'checking';
  const rows = layout === 'rows';

  // W13: stable, unique per row — see `uniqueRowIds`.
  const blockerRowIds = uniqueRowIds(blockers, readinessBlockerRowId);
  const warningRowIds = uniqueRowIds(warnings, readinessWarningRowId);

  const facts = buildReadinessFacts({
    result,
    analysisName,
    routeNames,
    socialProof,
    insightVariablesAccepted,
    insightTheses,
    layout,
  });

  const headerCaption = rows
    ? (blocked
      ? `Computed from the settings below — fix the blocker${blockers.length === 1 ? '' : 's'} to enable Create with AI.`
      : 'Computed from the settings below — the same check the server runs before any task starts.')
    : (blocked
      ? `Computed from the selection that will be sent. Fix the blocker${blockers.length === 1 ? '' : 's'} to enable Create with AI.`
      : 'Computed from the selection that will be sent; updates as you change source data.');

  /**
   * A blocked strip must never be a dead end (FP-6): the inline fix is the
   * one-click way out where it exists, and «Adjust in Source data» is the way
   * out everywhere else — including «no analysis pinned + no routes», where no
   * route set exists to select. The ROWS layout needs neither: every fact row
   * carries an Edit straight to the step that owns the value.
   */
  /** MF-5: the thesis blocker's own way out, offered only where it exists. */
  const canFixTheses = (blocker: FeasibilityBlocker): boolean =>
    blocker.code === 'task_without_theses' && Boolean(onSelectAllTheses);

  const hasInlineFix = blockers.some(
    (blocker) => (blocker.fix === 'select_routes' && routeNames.length > 0) || canFixTheses(blocker),
  );

  const renderFix = (blocker: FeasibilityBlocker): React.ReactNode => {
    if (canFixTheses(blocker)) {
      const taskName = blocker.taskId ? insightScopeNames?.[blocker.taskId] : undefined;
      return (
        <Button
          size="small"
          variant={rows ? 'contained' : 'outlined'}
          onClick={() => onSelectAllTheses?.(blocker.taskId)}
          // Fix round 3: `task_without_theses` is emitted ONCE PER STARVED SCOPE
          // (`strategyFeasibility`), and both layouts render a fix per blocker —
          // so two starved tasks used to paint two buttons with the same id.
          // Keyed by scope, exactly like the step's own Alert.
          data-testid={STRATEGY_LIST.readinessFixScope(blocker.taskId ?? GLOBAL_INSIGHT_SCOPE)}
        >
          {taskName ? `Select all theses of "${taskName}"` : 'Select all theses'}
        </Button>
      );
    }
    if (blocker.fix !== 'select_routes' || routeNames.length === 0) return null;
    return (
      <Button
        size="small"
        // Rows layout: the fix is CONTAINED inside the blocker panel, which is
        // the only contained action outside the footer primary (FP-6 — one
        // contained primary per surface, and this one is the way out of a
        // blocked state).
        variant={rows ? 'contained' : 'outlined'}
        onClick={onSelectAllRoutes}
        data-testid="strategy-readiness-fix"
      >
        {/* Create-mode vocabulary is «tasks» (the step is named Tasks); the
            shipped strip keeps saying «routes» so edit mode and the specs that
            read it stay untouched. */}
        {rows
          ? (routeNames.length === 1 ? 'Select the task' : `Select all ${routeNames.length} tasks`)
          : (routeNames.length === 1 ? 'Select the route' : `Select all ${routeNames.length} routes`)}
      </Button>
    );
  };

  // One verdict per surface: where a launch status is supplied it wins, because
  // it is the same value that disables the primary (m46).
  const chipLabel = launchStatus ? launchStatus.label : readinessStatusLabel(result);
  const chipBlocked = launchStatus ? launchStatus.blocked : blocked;
  const chipChecking = chipLabel === 'Checking…';

  const statusChip = (
    <Chip
      data-testid="strategy-readiness-status"
      size="small"
      icon={chipChecking
        ? <HourglassEmptyIcon fontSize="small" />
        : chipBlocked
          ? <ErrorOutlineIcon fontSize="small" />
          : <CheckCircleOutlineIcon fontSize="small" />}
      label={chipLabel}
      color={chipBlocked && !chipChecking ? 'error' : chipChecking ? 'default' : 'primary'}
      variant={chipBlocked && !chipChecking ? 'filled' : 'outlined'}
      sx={{ height: 28, '& .MuiChip-label': { fontSize: '0.875rem', fontWeight: 500 } }}
    />
  );

  /** AC-017 honesty: the task-scoped variables did not disappear, they are
      simply out of scope while no route carries them. */
  const taskScopedNote = !checking && result.facts.routes === 0 && routeNames.length > 0
    ? `${taskScopedVariablesWhenSelected === undefined
      ? 'Task-scoped variables'
      : `${taskScopedVariablesWhenSelected} task-scoped variables`}`
      + ' are not sent while no route is selected.'
      + ' They return automatically when routes are selected.'
    : null;

  if (rows) {
    const blockedFactKeys = new Set<ReadinessFactKey>(
      blockers.flatMap((blocker): ReadinessFactKey[] => {
        if (blocker.code === 'no_task_routes') return ['routes'];
        if (blocker.code === 'analysis_not_pinned') return ['analysis'];
        if (blocker.code === 'empty_variable_package') return ['variables'];
        return [];
      }),
    );

    return (
      <Paper
        component="section"
        variant="outlined"
        aria-label="Generation readiness"
        data-testid={dataTestId}
        sx={{ p: 2, borderColor: blocked ? 'error.main' : 'divider' }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
          {statusChip}
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
            {headerCaption}
          </Typography>
        </Stack>

        {/* The blocker comes FIRST, as a panel with its fix contained in it —
            never below the facts it invalidates (owner ruling 2026-09-10).
            `Alert severity="error"` carries the MUI error-Alert colour PAIR,
            which is AA in both themes — a raw `error.light` fill with white
            text is 3.7:1 and fails (designer invariant 8, error twin). */}
        {blockers.map((blocker, index) => {
          const { title, detail } = splitBlockerMessage(blocker.message);
          const fix = renderFix(blocker);
          return (
            <Alert
              key={blockerRowIds[index]}
              severity="error"
              variant="standard"
              icon={<ErrorOutlineIcon fontSize="small" />}
              id={blockerRowIds[index]}
              data-testid={blockerRowIds[index]}
              action={fix ?? undefined}
              sx={{ mt: 1.5, alignItems: 'center', '& .MuiAlert-action': { alignItems: 'center', pt: 0 } }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{title}</Typography>
              {detail ? <Typography variant="body2">{detail}</Typography> : null}
            </Alert>
          );
        })}

        <Box sx={{ mt: 1.5 }}>
          {facts.map((fact) => (
            <FactRow
              key={fact.key}
              fact={fact}
              blocked={blockedFactKeys.has(fact.key)}
              onEdit={onEdit}
            />
          ))}
        </Box>

        {taskScopedNote && (
          <ReadinessRow
            testId="strategy-readiness-task-scoped-note"
            icon={<InfoOutlinedIcon fontSize="small" sx={{ color: 'info.main' }} />}
            detail={taskScopedNote}
          />
        )}

        {warnings.map((warning, index) => (
          <ReadinessRow
            key={warningRowIds[index]}
            testId={warningRowIds[index]}
            icon={<WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />}
            detail={warning.message}
          />
        ))}
      </Paper>
    );
  }

  return (
    <Paper
      component="section"
      variant="outlined"
      aria-label="Generation readiness"
      data-testid={dataTestId}
      sx={{
        p: 2,
        borderColor: blocked ? 'error.main' : 'divider',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
        {statusChip}
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
          {headerCaption}
        </Typography>
        {!hasInlineFix && (
          <Button
            size="small"
            variant="text"
            onClick={onAdjust}
            data-testid="strategy-readiness-adjust"
          >
            Adjust in Source data
          </Button>
        )}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 2,
          mt: 1.5,
        }}
      >
        {facts.map((fact) => (
          <Fact
            key={fact.key}
            testId={`strategy-readiness-fact-${fact.key}`}
            label={fact.label}
            value={fact.value}
            detail={fact.detail}
          />
        ))}
      </Box>

      {blockers.map((blocker, index) => {
        const { title, detail } = splitBlockerMessage(blocker.message);
        return (
          <ReadinessRow
            key={blockerRowIds[index]}
            id={blockerRowIds[index]}
            testId={blockerRowIds[index]}
            icon={<ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />}
            title={title}
            detail={detail}
            action={renderFix(blocker)}
          />
        );
      })}

      {taskScopedNote && (
        <ReadinessRow
          testId="strategy-readiness-task-scoped-note"
          icon={<InfoOutlinedIcon fontSize="small" sx={{ color: 'info.main' }} />}
          detail={taskScopedNote}
        />
      )}

      {warnings.map((warning, index) => (
        <ReadinessRow
          key={warningRowIds[index]}
          testId={warningRowIds[index]}
          icon={<WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />}
          detail={warning.message}
        />
      ))}
    </Paper>
  );
};

export default GenerationReadinessStrip;
